import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useBetting } from '../../context/BettingContext';
import { useNavigate, useParams } from 'react-router-dom';
import { COMPETITIONS, MARKET_TYPES } from '../../data/mockData';
import Avatar from '../shared/Avatar';
import TeamTag from '../shared/TeamTag';
import './Dashboard.css';

function LivePotentialCard({ potential }) {
  if (!potential || potential.activeBets === 0) {
    return (
      <div className="dash-card dash-potential dash-potential--empty">
        <h3>Live Bet Potential</h3>
        <p className="dash-potential-empty">No active bets yet. Hit the betting page!</p>
      </div>
    );
  }

  return (
    <div className="dash-card dash-potential">
      <h3>Live Bet Potential</h3>
      <div className="dash-potential-grid">
        <div className="dash-potential-stat">
          <span className="dash-potential-stat-value">{potential.activeBets}</span>
          <span className="dash-potential-stat-label">Active Bets</span>
        </div>
        <div className="dash-potential-stat">
          <span className="dash-potential-stat-value">{potential.totalStaked.toLocaleString()}</span>
          <span className="dash-potential-stat-label">Total Staked</span>
        </div>
        <div className="dash-potential-stat dash-potential-stat--highlight">
          <span className="dash-potential-stat-value">+{potential.totalPotentialWin.toLocaleString()}</span>
          <span className="dash-potential-stat-label">Potential Win</span>
        </div>
        <div className="dash-potential-stat">
          <span className="dash-potential-stat-value">{potential.avgOdds}x</span>
          <span className="dash-potential-stat-label">Avg Odds</span>
        </div>
      </div>
      <div className="dash-potential-bar">
        <div className="dash-potential-bar-fill" style={{
          width: `${Math.min((potential.potentialProfit / potential.totalStaked) * 100, 100)}%`
        }}></div>
      </div>
      <p className="dash-potential-profit">
        Potential Profit: <strong>+{potential.potentialProfit.toLocaleString()}</strong> coins
      </p>
    </div>
  );
}

function MyBetsCard({ bets, markets }) {
  const userBets = bets.filter((b) => b.status === 'pending');

  if (userBets.length === 0) {
    return (
      <div className="dash-card">
        <h3>My Active Bets</h3>
        <p className="dash-empty-text">No active bets. Time to make some picks!</p>
      </div>
    );
  }

  return (
    <div className="dash-card">
      <h3>My Active Bets</h3>
      <div className="dash-bets-list">
        {userBets.slice(0, 5).map((bet) => {
          const market = markets.find((m) => m.id === bet.marketId);
          const typeInfo = market ? MARKET_TYPES[Object.keys(MARKET_TYPES).find(
            (k) => MARKET_TYPES[k].id === market.type
          )] : null;
          return (
            <div key={bet.id} className="dash-bet-item">
              <div className="dash-bet-item-left">
                <span className="dash-bet-item-icon">{typeInfo?.icon || '⚽'}</span>
                <div>
                  <div className="dash-bet-item-match">{market?.matchName || 'Unknown'}</div>
                  <div className="dash-bet-item-pick">{bet.optionName}</div>
                </div>
              </div>
              <div className="dash-bet-item-right">
                <div className="dash-bet-item-stake">{bet.amount} coins</div>
                <div className="dash-bet-item-potential">→ {bet.potentialWin}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UpcomingMatchesCard({ markets, rounds }) {
  const openRound = rounds.find((r) => r.status === 'open');
  const openMarkets = openRound
    ? markets.filter((m) => m.roundId === openRound.id && m.type === 'match_result')
    : [];

  return (
    <div className="dash-card dash-matches">
      <div className="dash-card-header">
        <h3>Upcoming Matches</h3>
        {openRound && <span className="dash-round-badge">Round {openRound.roundNumber} - {openRound.name}</span>}
      </div>
      {openMarkets.length === 0 ? (
        <p className="dash-empty-text">No open matches right now.</p>
      ) : (
        <div className="dash-matches-list">
          {openMarkets.map((market) => {
            const comp = COMPETITIONS[Object.keys(COMPETITIONS).find(
              (k) => COMPETITIONS[k].id === market.competition
            )];
            return (
              <div key={market.id} className="dash-match-item">
                <div className="dash-match-comp" style={{ color: comp?.color }}>
                  {comp?.emoji} {comp?.name}
                </div>
                <div className="dash-match-name">{market.matchName}</div>
                <div className="dash-match-date">
                  {new Date(market.matchDate).toLocaleDateString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </div>
                <div className="dash-match-odds">
                  {market.options.map((opt) => (
                    <span key={opt.name} className="dash-match-odd">
                      <small>{opt.name.replace(/\s*Win$/, '')}</small>
                      <strong>{opt.odds}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const QUICK_EMOJIS = ['🔥', '😂', '🤡', '💀', '🧠', '😬', '💰', '🎯'];

function ActivityItem({ bet, market, obfuscated, currentUserId }) {
  const { members: allUsers = [] } = useBetting();
  const betUser = allUsers.find((u) => u.id === bet.userId);
  const { reactions: allReactions, addReaction, addBetComment } = useBetting();
  const reactions = allReactions.filter(r => r.betId === bet.id);
  const [error, setError] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState('');

  const handleEmoji = async emoji => {
    try { await addReaction(bet.id, emoji); setError(''); } catch (err) { setError(err.message); }
  };
  const handleComment = async () => {
    if (!commentText.trim()) return;
    try { await addBetComment(bet.id, commentText.trim()); setCommentText(''); setShowComment(false); setError(''); } catch (err) { setError(err.message); }
  };
  const emojiReactions = reactions.filter((r) => r.type === 'emoji');
  const comments = reactions.filter((r) => r.type === 'comment');

  // Group emojis
  const emojiCounts = {};
  emojiReactions.forEach((r) => {
    if (!emojiCounts[r.emoji]) emojiCounts[r.emoji] = { count: 0, mine: false };
    emojiCounts[r.emoji].count++;
    if (r.userId === currentUserId) emojiCounts[r.emoji].mine = true;
  });

  return (
    <div className="dash-activity-item-wrap">{error && <p role="alert" className="mea-error">{error}</p>}
      <div className="dash-activity-item">
        <Avatar user={betUser} size={18} className="dash-activity-avatar" />
        <div className="dash-activity-text">
          <strong>{betUser?.displayName || 'Someone'}</strong><TeamTag user={betUser} /> bet {bet.amount} coins
          {obfuscated
            ? <em> on a market</em>
            : <> on <em>{market?.matchName || 'a match'}</em></>
          }
        </div>
      </div>

      {/* Existing reactions */}
      {(Object.keys(emojiCounts).length > 0 || comments.length > 0) && (
        <div className="dash-reactions">
          {Object.entries(emojiCounts).map(([emoji, data]) => (
            <button
              key={emoji}
              className={`dash-reaction-badge ${data.mine ? 'dash-reaction-badge--mine' : ''}`}
              onClick={() => handleEmoji(emoji)}
            >
              {emoji} {data.count > 1 && <span>{data.count}</span>}
            </button>
          ))}
          {comments.map((c) => {
            const cUser = allUsers.find((u) => u.id === c.userId);
            return (
              <div key={c.id} className="dash-comment">
                <Avatar user={cUser} size={18} className="dash-comment-avatar" />
                <span className="dash-comment-text"><strong>{cUser?.displayName}</strong> {c.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* React bar */}
      <div className="dash-react-bar">
        {QUICK_EMOJIS.map((emoji) => (
          <button key={emoji} className="dash-react-btn" onClick={() => handleEmoji(emoji)} title={emoji}>
            {emoji}
          </button>
        ))}
        <button className="dash-react-btn dash-react-btn--comment" onClick={() => setShowComment(!showComment)} title="Comment">
          💬
        </button>
      </div>

      {showComment && (
        <div className="dash-comment-input-row">
          <input
            type="text"
            className="dash-comment-input"
            placeholder="Say something..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleComment()}
            autoFocus
          />
          <button className="dash-comment-send" onClick={handleComment} disabled={!commentText.trim()}>Send</button>
        </div>
      )}
    </div>
  );
}

function FriendsActivityCard({ bets, markets, obfuscated, currentUserId }) {
  const recentBets = [...bets]
    .filter((b) => b.placedAt)
    .sort((a, b) => new Date(b.placedAt) - new Date(a.placedAt))
    .slice(0, 8);

  return (
    <div className="dash-card dash-card--activity">
      <div className="dash-card-header">
        <h3>Friends Activity</h3>
        {obfuscated && <span className="dash-obfuscated-badge">🙈 Picks Hidden</span>}
      </div>
      {recentBets.length === 0 ? (
        <p className="dash-empty-text">No recent activity from friends.</p>
      ) : (
        <div className="dash-activity-list">
          {recentBets.map((bet) => {
            const market = markets.find((m) => m.id === bet.marketId);
            return (
              <ActivityItem
                key={bet.id}
                bet={bet}
                market={market}
                obfuscated={obfuscated}
                currentUserId={currentUserId}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { id: masterEventId, actId } = useParams();
  const { user } = useAuth();
  const { events, rounds, markets, bets, livePotential, settings, isMyBet, participant, balance } = useBetting();
  const navigate = useNavigate();
  const userBets = bets.filter(isMyBet);
  const activeEvent = events.find((e) => e.status === 'active');
  const activeRound = rounds.find((r) => r.status === 'open');

  return (
    <div className="dashboard">
      <div className="dash-header">
        <div>
          <h2>Welcome back, {user?.displayName}! <Avatar user={user} size={20} style={{display:'inline-block',verticalAlign:'middle'}} /></h2>
          {activeEvent && activeRound ? (
            <p className="dash-header-sub">
              {activeEvent.name} — Round {activeRound.roundNumber}: {activeRound.name}
            </p>
          ) : (
            <p className="dash-header-sub">Ready to make some calls?</p>
          )}
        </div>
        <button className="dash-cta" onClick={() => navigate(`/me/${masterEventId}/activity/${actId}/betting/place`)}>
          Place a Bet →
        </button>
      </div>

      <p>{participant?.name || user?.displayName} · {balance?.toLocaleString()} coins</p>
      <div className="dash-grid">
        <LivePotentialCard potential={livePotential} />
        <MyBetsCard bets={userBets} markets={markets} />
        <UpcomingMatchesCard markets={markets} rounds={rounds} />
        <FriendsActivityCard bets={bets} markets={markets} obfuscated={settings.obfuscateActivity} currentUserId={user?.id} />
      </div>
    </div>
  );
}
